export interface ParsedMailto {
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  body?: string;
}

const decodeComponent = (value: string): string => {
  let current = value;
  for (let i = 0; i < 5; i++) {
    if (!/%[0-9A-Fa-f]{2}/.test(current)) {
      break;
    }
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) {
        break;
      }
      current = decoded;
    } catch {
      break;
    }
  }
  return current;
};

const splitAddresses = (value: string): string[] =>
  value
    .split(',')
    .map((address) => decodeComponent(address.trim()))
    .filter((address) => address.length > 0);

export const parseMailto = (href: string): ParsedMailto => {
  const withoutProtocol = (href ?? '').replace(/^mailto:/i, '');
  const queryIndex = withoutProtocol.indexOf('?');
  const rawTo =
    queryIndex >= 0 ? withoutProtocol.slice(0, queryIndex) : withoutProtocol;
  const queryString =
    queryIndex >= 0 ? withoutProtocol.slice(queryIndex + 1) : '';

  const result: ParsedMailto = { to: splitAddresses(rawTo) };

  for (const pair of queryString.split('&')) {
    if (!pair) {
      continue;
    }
    const eqIndex = pair.indexOf('=');
    const rawKey = eqIndex >= 0 ? pair.slice(0, eqIndex) : pair;
    const rawValue = eqIndex >= 0 ? pair.slice(eqIndex + 1) : '';
    const key = decodeComponent(rawKey).toLowerCase();

    switch (key) {
      case 'to': {
        result.to.push(...splitAddresses(rawValue));
        break;
      }
      case 'cc': {
        const addresses = splitAddresses(rawValue);
        if (addresses.length) {
          result.cc = addresses;
        }
        break;
      }
      case 'bcc': {
        const addresses = splitAddresses(rawValue);
        if (addresses.length) {
          result.bcc = addresses;
        }
        break;
      }
      case 'subject': {
        result.subject = decodeComponent(rawValue);
        break;
      }
      case 'body': {
        result.body = decodeComponent(rawValue);
        break;
      }
      default:
        break;
    }
  }

  return result;
};

const htmlEscapes: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

export const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
